import _ from "lodash";
import {
	buildGlyphRecoveryMap,
	recoveryEntryCount,
	codeMapForFont,
	recoverItemText,
} from "@/utils/pdfGlyphRecovery";

/**
 * Initializes WPD Page Manager.
 */
function init(wpd, wpdWindow, wpdDocument, dataEntryVM) {
	wpd.PDFManager = class extends wpd.PageManager {
		constructor() {
			super();

			this.textLayer = wpdDocument.getElementById("pdf-text-layer");

			// add debounced version of class method
			this.renderTextLayer = _.debounce(this.#renderTextLayer.bind(this), 300);

			// attach scroll listener to graphics container
			this.attachScrollHandler();
		}

		static lowQualityScale = 3;
		static highQualityScale = 6;

		textLayer;

		// per-file cache of recovered Math-Pi glyphs (BaseFont -> code -> char).
		// Empty for normal PDFs, so recovery is a no-op outside the affected docs.
		#glyphRecoveryByFile = new Map();

		#currentScale = this.defaultScale;

		get #scale() {
			return this.#currentScale;
		}

		set #scale(newScale) {
			this.#currentScale = newScale;
		}

		get defaultScale() {
			return wpd.PDFManager.lowQualityScale;
		}

		get scaleFactor() {
			return wpd.PDFManager.highQualityScale / wpd.PDFManager.lowQualityScale;
		}

		get currentScaleFactor() {
			return this.#scale / wpd.PDFManager.lowQualityScale;
		}

		resetScale() {
			this.#scale = this.defaultScale;
		}

		isHighQualityRender() {
			return this.currentScaleFactor > 1;
		}

		getPage(pageNumber) {
			return this.handle.getPage(pageNumber);
		}

		getPageLabels() {
			return this.handle.getPageLabels();
		}

		pageCount() {
			return this.handle.numPages;
		}

		renderHighQualityPage(pageNumber) {
			this.#scale = wpd.PDFManager.highQualityScale;
			return this.renderPage(pageNumber, true);
		}

		renderLowQualityPage(pageNumber) {
			this.#scale = wpd.PDFManager.lowQualityScale;
			return this.renderPage(pageNumber, true);
		}

		async switch(pageNumber = 1, skipRaster = false) {
			// reset scale
			this.resetScale();

			return await super.switch(pageNumber, skipRaster);
		}

		async #rasterizePage(page) {
			const viewport = page.getViewport({
				scale: this.#scale,
			});

			const $canvas = wpdDocument.createElement("canvas");
			const context = $canvas.getContext("2d");

			$canvas.width = Math.round(viewport.width);
			$canvas.height = Math.round(viewport.height);

			$canvas.style.width = viewport.width + "px";
			$canvas.style.height = viewport.height + "px";

			await page.render({
				canvasContext: context,
				viewport: viewport,
			}).promise;

			return $canvas;
		}

		async _pageRenderer(page, isQualityChange, resolve, reject) {
			const self = this;

			try {
				const $canvas = await this.#rasterizePage(page);

				wpd.imageManager.loadFromCanvas($canvas, isQualityChange);

				wpd.graphicsWidget.setRotation(self.getRotation());
				wpd.graphicsWidget.rotateAndResize();

				await this.renderTextLayer();

				resolve();
			} catch (error) {
				console.log(error);
				wpd.busyNote.close();
				reject(error);
			}
		}

		attachScrollHandler() {
			const container = wpdDocument.getElementById("graphicsContainer");

			container.addEventListener(
				"scroll",
				function () {
					this.updateTextLayerScroll();
				}.bind(this),
			);
		}

		updateTextLayerScroll() {
			const container = wpdDocument.getElementById("graphicsContainer");
			const canvasDiv = wpdDocument.getElementById("canvasDiv");

			const { scrollTop, scrollLeft } = container;
			const { top: containerTop, left: containerLeft } =
				container.getBoundingClientRect();
			const { top: canvasTop, left: canvasLeft } =
				canvasDiv.getBoundingClientRect();

			this.textLayer.style.top = canvasTop - containerTop + scrollTop + "px";
			this.textLayer.style.left =
				canvasLeft - containerLeft + scrollLeft + "px";
		}

		async #renderTextLayer() {
			const canvasDiv = wpdDocument.getElementById("canvasDiv");

			this.textLayer.replaceChildren();

			this.textLayer.style.width = canvasDiv.clientWidth + "px";
			this.textLayer.style.height = canvasDiv.clientHeight + "px";

			this.updateTextLayerScroll();

			const rotation = wpd.graphicsWidget.getRotation();
			const scale = this.#scale * wpd.graphicsWidget.getZoomRatio();

			if (dataEntryVM.isUnmounted) return;

			// Prefer outer pdfjs v4 when available - it emits word-level items,
			// which fixes direct-select-text on PDFs with tab-separated justified
			// lines (WPD's bundled pdfjs 2.9 merges those into one span). Fall back
			// to WPD's pdfjs if the hand-off is unavailable.
			const outer = wpdWindow.parent?.__outerPdfjsV4;
			if (outer && (await this.#renderTextLayerWithOuter(outer, rotation))) {
				return;
			}

			const page = await this.getPage(this.currentPage());
			const viewport = page.getViewport({ rotation, scale });
			await wpdWindow.pdfjsLib.renderTextLayer({
				textContentStream: page.streamTextContent(),
				container: this.textLayer,
				viewport: viewport,
				textDivs: [],
				enhanceTextSelection: true,
			}).promise;
		}

		async #renderTextLayerWithOuter(outer, rotation) {
			const fileIndex = wpd.appData.getFileManager().currentFileIndex() ?? 0;
			const docPromise = outer.getDoc(fileIndex);
			if (!docPromise) return false;

			let doc;
			try {
				doc = await docPromise;
			} catch {
				return false;
			}
			if (!doc || dataEntryVM.isUnmounted) return false;

			const page = await doc.getPage(this.currentPage());

			// Match the text-layer coordinate system to the displayed canvas. WPD's
			// internal render scale (this.#scale * zoomRatio) targets a high-DPI
			// bitmap that then gets downscaled to the canvas's CSS size; positioning
			// spans by that would put them outside the visible text layer. Derive
			// scale from the container dimensions instead.
			const container = this.textLayer;
			const unit = page.getViewport({ scale: 1, rotation });
			const scale = container.clientWidth / unit.width;
			const viewport = page.getViewport({ scale, rotation });

			// v4's TextLayer sizes the container via `var(--scale-factor) * pageWidth px`
			// and positions spans as percentages of that — so --scale-factor must match
			// our chosen viewport scale for the layer to overlay the canvas correctly.
			container.style.setProperty("--scale-factor", String(scale));

			const textContent = await page.getTextContent();

			// Recover math symbols (>=, <=, >, <, +/-, ...) that this PDF's fonts
			// drop to control characters because they carry no ToUnicode map and
			// use Adobe's "H#####" glyph names. Mutates item.str in place so the
			// selectable text layer - and therefore copied text - is correct.
			await this.#recoverDroppedGlyphs(doc, page, textContent, fileIndex);

			if (dataEntryVM.isUnmounted) return true;

			const textLayer = new outer.pdfjsLib.TextLayer({
				textContentSource: textContent,
				container,
				viewport,
			});
			await textLayer.render();
			return true;
		}

		// Build (once per file) the BaseFont -> {code -> char} recovery table by
		// parsing the raw PDF, since pdf.js doesn't expose /Differences. Returns
		// an empty map for PDFs with no recoverable Math-Pi fonts.
		async #glyphRecoveryFor(doc, fileIndex) {
			if (this.#glyphRecoveryByFile.has(fileIndex)) {
				return this.#glyphRecoveryByFile.get(fileIndex);
			}
			let recovery = new Map();
			try {
				recovery = buildGlyphRecoveryMap(await doc.getData());
			} catch {
				recovery = new Map();
			}
			this.#glyphRecoveryByFile.set(fileIndex, recovery);
			return recovery;
		}

		async #recoverDroppedGlyphs(doc, page, textContent, fileIndex) {
			const recovery = await this.#glyphRecoveryFor(doc, fileIndex);
			if (recoveryEntryCount(recovery) === 0) return;

			// Resolving the operator list populates commonObjs so each item's
			// font id can be mapped to its real BaseFont name. Only paid on the
			// affected (Math-Pi) PDFs thanks to the guard above.
			try {
				await page.getOperatorList();
			} catch {
				return;
			}

			const codeMapByFontId = new Map();
			for (const item of textContent.items) {
				if (!item.str || item.fontName == null) continue;

				let codeMap = codeMapByFontId.get(item.fontName);
				if (codeMap === undefined) {
					let baseName = null;
					try {
						baseName = page.commonObjs.get(item.fontName)?.name ?? null;
					} catch {
						baseName = null;
					}
					codeMap = baseName ? codeMapForFont(recovery, baseName) : null;
					codeMapByFontId.set(item.fontName, codeMap);
				}

				if (codeMap) item.str = recoverItemText(item.str, codeMap);
			}
		}
	};
}

export { init };
